import { useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  FacebookIcon,
  TwitterIcon,
  InstagramIcon,
  LinkedinIcon,
  YoutubeIcon
} from "lucide-react";
import MapSection from "@/components/map-section";

const contactFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  subject: z.string().min(1, { message: "Please select a subject" }),
  message: z.string().min(10, { message: "Message must be at least 10 characters" }),
  consent: z.boolean().refine(val => val === true, {
    message: "You must agree to receive communications",
  })
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      consent: false
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: Omit<ContactFormValues, "consent">) => {
      const response = await apiRequest("POST", "/api/contact", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Message Sent",
        description: "Thank you for your message. We'll get back to you as soon as possible.",
        variant: "default",
      });
      form.reset();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  function onSubmit(data: ContactFormValues) {
    setIsSubmitting(true);
    const { consent, ...contactData } = data;
    contactMutation.mutate(contactData);
  }

  const socialLinks = [
    { icon: <FacebookIcon className="h-5 w-5" />, url: "#", label: "Facebook" },
    { icon: <TwitterIcon className="h-5 w-5" />, url: "#", label: "Twitter" },
    { icon: <InstagramIcon className="h-5 w-5" />, url: "#", label: "Instagram" },
    { icon: <YoutubeIcon className="h-5 w-5" />, url: "#", label: "YouTube" },
    { icon: <LinkedinIcon className="h-5 w-5" />, url: "#", label: "LinkedIn" }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - Varsha Seeds</title>
        <meta name="description" content="Get in touch with Varsha Seeds for inquiries about our agricultural seeds, services, or to request a quote. Our team is ready to assist you." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary relative overflow-hidden py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YWdyaWN1bHR1cmFsIGxhbmRzY2FwZXN8fHx8fHwxNjk3ODM5MDgx&ixlib=rb-4.0.3&q=80&w=1920" 
            alt="Agricultural landscape" 
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-6">
            Get In Touch
          </h1>
          <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
            Have questions about our seeds or services? We're here to help you.
            Reach out to us for any inquiries or support.
          </p>
        </div>
      </section>
      
      {/* Contact Form & Info Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6 font-heading">Send Us a Message</h2>
              
              <Form {...form}>
                <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name*</FormLabel>
                          <FormControl>
                            <Input placeholder="Your name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address*</FormLabel>
                          <FormControl>
                            <Input placeholder="your@email.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input placeholder="Your phone number" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject*</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a subject" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="product">Product Inquiry</SelectItem>
                              <SelectItem value="service">Service Inquiry</SelectItem>
                              <SelectItem value="support">Technical Support</SelectItem>
                              <SelectItem value="quote">Request a Quote</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message*</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="How can we help you?" 
                            className="resize-none h-32"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="consent"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 py-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            I agree to receive communications from Varsha Seeds in accordance with their privacy policy.
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Form>
            </div>
            
            <div>
              <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                <h2 className="text-2xl font-bold mb-6 font-heading">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      <MapPin className="text-xl text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 font-heading">Office Address</h3>
                      <p className="text-neutral-700">
                        123 Agricultural Zone, Seed Processing Complex, Mumbai - 400001, Maharashtra, India
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      <Phone className="text-xl text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 font-heading">Phone Number</h3>
                      <p className="text-neutral-700">+91 1234 567 890</p>
                      <p className="text-neutral-700">+91 9876 543 210</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      <Mail className="text-xl text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 font-heading">Email Address</h3>
                      <p className="text-neutral-700">info@varshaseeds.com</p>
                      <p className="text-neutral-700">support@varshaseeds.com</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      <Clock className="text-xl text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 font-heading">Working Hours</h3>
                      <p className="text-neutral-700">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-neutral-700">Saturday: 9:00 AM - 1:00 PM</p>
                      <p className="text-neutral-700">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold mb-6 font-heading">Follow Us</h2>
                <p className="text-neutral-700 mb-4">
                  Stay updated with our latest products, farming tips, and agricultural news.
                </p>
                
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a 
                      key={index}
                      href={social.url} 
                      className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark transition" 
                      aria-label={social.label}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Frequently Asked Questions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-primary font-medium">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Frequently Asked Questions
            </h2>
            <p className="text-neutral-700">
              Find answers to common questions about our products and services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                What seed varieties do you offer?
              </h3>
              <p className="text-neutral-700">
                We offer a wide range of seed varieties including cereals (wheat, rice, maize), 
                pulses (chickpeas, lentils), vegetables, and oil seeds (mustard, sunflower). 
                Visit our products page for the complete catalog.
              </p>
            </div>
            
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                Do you provide delivery services?
              </h3>
              <p className="text-neutral-700">
                Yes, we deliver our products across India. Delivery times vary based on location. 
                For bulk orders, we offer special logistics arrangements. Contact our team for details.
              </p>
            </div>
            
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                How can I get technical support for seed cultivation?
              </h3>
              <p className="text-neutral-700">
                Our technical experts provide comprehensive support from planting to harvest. 
                You can reach them through our customer service line or submit a support request 
                through the contact form.
              </p>
            </div>
            
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                Do you offer bulk purchase discounts?
              </h3>
              <p className="text-neutral-700">
                Yes, we offer special pricing for bulk purchases. The discount varies based on 
                the quantity and type of seeds. Please contact our sales team for a customized quote.
              </p>
            </div>
            
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                What is your seed quality guarantee?
              </h3>
              <p className="text-neutral-700">
                All our seeds undergo rigorous quality testing for germination rate, purity, and vigor. 
                We guarantee the quality parameters mentioned on the packaging and provide replacement 
                for any product that doesn't meet these standards.
              </p>
            </div>
            
            <div className="bg-background p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 font-heading">
                Do you offer organic seed varieties?
              </h3>
              <p className="text-neutral-700">
                Yes, we have a growing selection of certified organic seed varieties. These are 
                produced without synthetic fertilizers or pesticides and are ideal for organic farming.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Map Section */}
      <MapSection />
    </>
  );
};

export default Contact;
